import React, { useEffect, useRef, useState } from "react";
import ClientAppShell from "../components/ClientAppShell";
import ContentLoader from "../components/ContentLoader";
import CapabilityAccessBanner from "./components/CapabilityAccessBanner";
import { useAuth } from "../auth/AuthContext";
import { submitCapabilities } from "../services/capabilitiesApi";
import {
  loadProjectCapabilities,
  loadSelectedCapabilities,
  loadSubCapabilityAssessmentLegacy,
} from "../services/clientProjectData";
import {
  exportAssessmentWorkbook,
  fetchRequestStatus,
  importAssessmentWorkbook,
  SubmitCapabilityRequest,
  submitSMEState,
} from "../services/assessment";
import { confirmAlert, showAlert } from "../components/AppAlert/appAlert";
import {
  assessmentUnitKey,
  isLeafCapability,
  sameAssessmentUnit,
  selectableUnitCount,
} from "../utils/assessmentUnitUtils";
import "../assets/css/capablities.css";
import "../assets/css/dashboard-content.css";
import "../assets/css/import-export.css";

const getSelectionKey = (capabilityId, subCapabilityId) =>
  assessmentUnitKey(capabilityId, subCapabilityId);

const toSortedSelectionKeys = (caps) =>
  [...caps]
    .map((cap) => getSelectionKey(cap.capabilityId, cap.subCapabilityId))
    .sort();

const areSelectionKeysEqual = (keysA, keysB) =>
  keysA.length === keysB.length && keysA.every((key, index) => key === keysB[index]);

const isFilledAssessment = (row) => {
  if (!row) {
    return false;
  }

  const currentRating = row.currentStatusRating ?? row.CurrentStatusRating;
  const futureRating = row.futureStatusRating ?? row.FutureStatusRating;
  const currentComment = String(
    row.currentStatusDescription ?? row.CurrentStatusDescription ?? ""
  ).trim();
  const futureComment = String(
    row.futureStatusDescription ?? row.FutureStatusDescription ?? ""
  ).trim();

  return Boolean(currentRating && futureRating && currentComment && futureComment);
};

const flattenSelectedSubCapabilities = (selectedResponse) =>
  selectedResponse?.data?.selectedCapabilities?.flatMap((cap) =>
    (cap.selectedSubCapabilities || []).map((subCap) => ({
      capabilityId: subCap.capabilityId ?? cap.capabilityId,
      subCapabilityId: subCap.subCapabilityId,
      state: subCap.state || "3",
      isNewLocked: subCap.isNewLocked ?? false,
      isApproved: subCap.state === "4",
      isNewlySelected: false,
    }))
  ) || [];

const ImportExport = () => {
  const basename = process.env.PUBLIC_URL || "";
  const { user } = useAuth();
  const importInputRef = useRef(null);

  const [selectedItem, setSelectedItem] = useState(null);
  const [projectId, setProjectId] = useState(null);
  const [userId, setUserId] = useState(null);
  const [capabilities, setCapabilities] = useState([]);
  const [selectedCapabilities, setSelectedCapabilities] = useState([]);
  const [baselineSelectionKeys, setBaselineSelectionKeys] = useState([]);
  const [userCanAddNewCapabilities, setUserCanAddNewCapabilities] = useState(false);
  const [disableAllSelection, setDisableAllSelection] = useState(false);
  const [visibleSection, setVisibleSection] = useState(null);
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [canSubmitToAdvisor, setCanSubmitToAdvisor] = useState(false);
  const [isWorkbookLocked, setIsWorkbookLocked] = useState(false);
  const [requestStatus, setRequestStatus] = useState("Not Requested");
  const [isRequestActive, setIsRequestActive] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [statusRefreshKey, setStatusRefreshKey] = useState(0);

  const handleMenuSelection = (item) => {
    setSelectedItem(item);
  };

  useEffect(() => {
    const storedProjectId = localStorage.getItem("projectId");
    if (storedProjectId) {
      setProjectId(parseInt(storedProjectId, 10));
    } else {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user?.userId) {
      setUserId(user.userId);
    }
  }, [user]);

  useEffect(() => {
    if (!projectId) {
      return;
    }

    const loadCatalog = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await loadProjectCapabilities(projectId);
        setCapabilities(response.data || []);
      } catch (loadError) {
        console.error("Error fetching capabilities:", loadError);
        setError("Failed to load capabilities data");
      } finally {
        setLoading(false);
      }
    };

    loadCatalog();
  }, [projectId]);

  useEffect(() => {
    if (!projectId || !userId) {
      return;
    }

    const loadLockState = async () => {
      try {
        const [selectedResponse, assessmentResponse] = await Promise.all([
          loadSelectedCapabilities({
            userId,
            projectId,
          }),
          loadSubCapabilityAssessmentLegacy({ userId, projectId }).catch(() => ({ data: [] })),
        ]);
        const selected = flattenSelectedSubCapabilities(selectedResponse);
        setSelectedCapabilities(selected);
        setBaselineSelectionKeys(toSortedSelectionKeys(selected));

        const allSubmitted =
          selected.length > 0 && selected.every((subCap) => subCap.state === "4");
        const allLocked =
          selected.length > 0 &&
          selected.every(
            (subCap) => subCap.state === "4" || subCap.isNewLocked
          );

        let requestStatusValue = "Not Requested";
        let requestActive = false;
        try {
          const requestResponse = await fetchRequestStatus({ userId, projectId });
          requestStatusValue = requestResponse?.data?.status || "Not Requested";
          requestActive = Boolean(requestResponse?.data?.isActive);
        } catch {
          requestStatusValue = "Not Requested";
          requestActive = false;
        }

        // Match Capabilities: an approved request re-opens selection even if
        // previously submitted rows are still individually locked.
        if (requestStatusValue === "Approved") {
          requestActive = true;
        } else if (allSubmitted) {
          requestActive = false;
        }

        setRequestStatus(requestStatusValue);
        setIsRequestActive(requestActive);

        const isNewUser =
          selected.length === 0 && requestStatusValue === "Not Requested";
        const capabilitiesUnlocked =
          isNewUser || requestStatusValue === "Approved";
        setUserCanAddNewCapabilities(capabilitiesUnlocked);
        setDisableAllSelection(
          !isNewUser && allSubmitted && requestStatusValue !== "Approved"
        );
        setIsWorkbookLocked(allLocked && !capabilitiesUnlocked);

        const assessmentRows = Array.isArray(assessmentResponse?.data)
          ? assessmentResponse.data
          : [];
        const allHaveRecords =
          selected.length > 0 &&
          selected.every((subCap) =>
            isFilledAssessment(
              assessmentRows.find((row) =>
                sameAssessmentUnit(
                  row.capabilityId ?? row.CapabilityId,
                  row.subCapabilityId ?? row.SubCapabilityId,
                  subCap.capabilityId,
                  subCap.subCapabilityId
                )
              )
            )
          );
        const hasOpenSelection = selected.some(
          (subCap) => subCap.state === "3" && !subCap.isNewLocked
        );
        setCanSubmitToAdvisor(allHaveRecords && hasOpenSelection && !allLocked);
      } catch (lockError) {
        console.error("Error loading assessment lock state:", lockError);
      }
    };

    loadLockState();
  }, [projectId, userId, statusRefreshKey]);

  const toggleVisibility = (sectionId) => {
    setVisibleSection((prev) => (prev === sectionId ? null : sectionId));
  };

  const currentSelectionKeys = toSortedSelectionKeys(selectedCapabilities);
  const hasSelectionChanged = !areSelectionKeysEqual(
    currentSelectionKeys,
    baselineSelectionKeys
  );
  const canExport =
    selectedCapabilities.length > 0 && !isWorkbookLocked && !isExporting;

  const handleCheckboxChange = (capabilityId, subCapabilityId, checked) => {
    const existing = selectedCapabilities.find((item) =>
      sameAssessmentUnit(
        item.capabilityId,
        item.subCapabilityId,
        capabilityId,
        subCapabilityId
      )
    );
    const isApproved = Boolean(existing?.isApproved || existing?.state === "4");
    const isItemLocked = Boolean(
      existing?.isNewLocked && requestStatus !== "Not Requested"
    );

    if (isApproved || isItemLocked || isWorkbookLocked || disableAllSelection) {
      return;
    }

    setSelectedCapabilities((prev) => {
      const existingIndex = prev.findIndex((item) =>
        sameAssessmentUnit(
          item.capabilityId,
          item.subCapabilityId,
          capabilityId,
          subCapabilityId
        )
      );

      if (existingIndex !== -1) {
        if (!checked) {
          const existingItem = prev[existingIndex];
          if (!existingItem.isApproved && !existingItem.isNewLocked) {
            return prev.filter((_, index) => index !== existingIndex);
          }
        }
        return prev;
      }

      if (!checked) {
        return prev;
      }

      return [
        ...prev,
        {
          capabilityId,
          subCapabilityId,
          state: "3",
          isNewLocked: false,
          isApproved: false,
          isNewlySelected: true,
        },
      ];
    });
  };

  const selectedCountForCapability = (capabilityId) =>
    selectedCapabilities.filter((item) => item.capabilityId === capabilityId)
      .length;

  const buildExportSelections = () =>
    capabilities.flatMap((capability) => {
      if (isLeafCapability(capability)) {
        const leafSelected = selectedCapabilities.some((item) =>
          sameAssessmentUnit(
            item.capabilityId,
            item.subCapabilityId,
            capability.id,
            null
          )
        );
        return leafSelected
          ? [
              {
                capability: capability.capabilityName,
                subCapability: capability.capabilityName,
              },
            ]
          : [];
      }

      return (capability.subCapabilities ?? [])
        .filter((subCapability) =>
          selectedCapabilities.some((item) =>
            sameAssessmentUnit(
              item.capabilityId,
              item.subCapabilityId,
              capability.id,
              subCapability.subId
            )
          )
        )
        .map((subCapability) => ({
          capability: capability.capabilityName,
          subCapability: subCapability.subCapabilityName,
        }));
    });

  const persistSelectionIfChanged = async () => {
    if (!hasSelectionChanged) {
      return true;
    }

    const updatedCapabilities = selectedCapabilities.map((cap) =>
      cap.isNewlySelected ? { ...cap, state: "3" } : cap
    );

    await submitCapabilities({
      userId,
      projectId,
      selectedCapabilities: updatedCapabilities,
    });

    setSelectedCapabilities(
      updatedCapabilities.map((cap) => ({ ...cap, isNewlySelected: false }))
    );
    setBaselineSelectionKeys(toSortedSelectionKeys(updatedCapabilities));
    return true;
  };

  const handleExport = async () => {
    if (isWorkbookLocked) {
      showAlert(
        "This assessment has been submitted. Export is locked until your advisor approves a request to add capabilities.",
        "warning"
      );
      return;
    }

    if (selectedCapabilities.length === 0) {
      setIsPickerOpen(true);
      showAlert("Select at least one capability to export.", "warning");
      return;
    }

    const selections = buildExportSelections();
    if (selections.length === 0) {
      showAlert("Select at least one capability to export.", "warning");
      return;
    }

    setIsExporting(true);
    try {
      await persistSelectionIfChanged();
      await exportAssessmentWorkbook({
        userId,
        projectId,
        selections,
      });
    } catch (exportError) {
      console.error("Error exporting workbook:", exportError);
      showAlert(
        exportError?.message || "We couldn't export the workbook. Please try again.",
        "error"
      );
    } finally {
      setIsExporting(false);
    }
  };

  const handleImportClick = () => {
    if (isWorkbookLocked) {
      showAlert(
        "This assessment has been submitted. Import is locked until your advisor approves a request to add capabilities.",
        "warning"
      );
      return;
    }

    importInputRef.current?.click();
  };

  const handleImportFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) {
      return;
    }

    if (!userId || !projectId) {
      showAlert("We couldn't identify your project. Please reopen it and try again.", "error");
      return;
    }

    setIsImporting(true);
    try {
      const response = await importAssessmentWorkbook({
        userId,
        projectId,
        file,
      });
      showAlert({
        type: "success",
        title: "Import complete",
        message: "Your assessment was saved. Submit it here when you are ready.",
        confirmText: "OK",
      });
      setStatusRefreshKey((key) => key + 1);
    } catch (importError) {
      console.error("Error importing workbook:", importError);
      showAlert({
        type: "error",
        title: "Import failed",
        message: "We could not import this workbook. Check the file and try again.",
        confirmText: "OK",
      });
    } finally {
      setIsImporting(false);
    }
  };

  const handleSubmitToAdvisor = async () => {
    if (!userId || !projectId) {
      showAlert("We couldn't identify your project. Please reopen it and try again.", "error");
      return;
    }

    if (!canSubmitToAdvisor || isWorkbookLocked) {
      showAlert(
        "Complete every selected sub-capability before submitting.",
        "warning"
      );
      return;
    }

    const confirmed = await confirmAlert({
      type: "warning",
      title: "Submit assessment?",
      message: "This sends your assessment to your advisor. You will not be able to import or export until new capabilities are approved.",
      confirmText: "Submit",
      cancelText: "Cancel",
    });

    if (!confirmed) {
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await submitSMEState({
        userId,
        projectId,
        state: 0,
      });
      if (response) {
        setCanSubmitToAdvisor(false);
        setIsWorkbookLocked(true);
        showAlert({
          type: "success",
          title: "Submitted",
          message: "Your assessment was submitted successfully.",
          confirmText: "OK",
        });
        setStatusRefreshKey((key) => key + 1);
      }
    } catch (submitError) {
      console.error("Error submitting assessment:", submitError);
      showAlert({
        type: "error",
        title: "Submit failed",
        message: "We could not submit your assessment. Please try again.",
        confirmText: "OK",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCapabilityRequest = async () => {
    if (!userId || !projectId) {
      showAlert("We couldn't identify your project. Please reopen it and try again.", "error");
      return;
    }

    setRequesting(true);
    try {
      const response = await SubmitCapabilityRequest({
        userId,
        projectId,
        status: "Pending",
      });
      if (response?.data) {
        setRequestStatus("Pending");
        showAlert("Your request to add capabilities has been sent to your advisor.", "success");
      } else {
        showAlert("We couldn't submit your request. Please try again.", "error");
      }
    } catch (requestError) {
      console.error("Error submitting capability request:", requestError);
      showAlert("We couldn't submit your request. Please try again.", "error");
    } finally {
      setRequesting(false);
    }
  };

  const renderPicker = () => {
    if (loading) {
      return <ContentLoader page label="Loading capabilities…" />;
    }

    if (error) {
      return <div className="cap-error">{error}</div>;
    }

    if (!projectId) {
      return (
        <div className="cap-error">
          We couldn&apos;t identify your project. Please reopen it and try again.
        </div>
      );
    }

    if (!capabilities.length) {
      return (
        <div className="cap-error">
          No capabilities are available to export for this project.
        </div>
      );
    }

    return (
      <div className="cap-frame-content impexp-picker">
        <div className="cap-message-section">
          <div className="cap-frame-parent4">
            <div className="cap-capabilities-list">
              {capabilities.map((capability) => {
                const leaf = isLeafCapability(capability);
                const capabilitySelectedCount =
                  selectedCountForCapability(capability.id);
                const capabilityHasSelection = capabilitySelectedCount > 0;
                const isCapabilityExpanded = visibleSection === capability.id;
                const leafSelected = selectedCapabilities.some((item) =>
                  sameAssessmentUnit(
                    item.capabilityId,
                    item.subCapabilityId,
                    capability.id,
                    null
                  )
                );
                const leafApproved = selectedCapabilities.some(
                  (item) =>
                    sameAssessmentUnit(
                      item.capabilityId,
                      item.subCapabilityId,
                      capability.id,
                      null
                    ) && (item.isApproved || item.state === "4")
                );
                const leafLocked = selectedCapabilities.some(
                  (item) =>
                    sameAssessmentUnit(
                      item.capabilityId,
                      item.subCapabilityId,
                      capability.id,
                      null
                    ) &&
                    item.isNewLocked &&
                    requestStatus !== "Not Requested"
                );
                const leafDisabled =
                  leafApproved ||
                  leafLocked ||
                  isWorkbookLocked ||
                  disableAllSelection;

                return (
                <div className="cap-capability-row" key={capability.id}>
                  <div
                    className={`cap-frame-parent5${
                      isCapabilityExpanded || (leaf && leafSelected)
                        ? " highlight"
                        : ""
                    }${capabilityHasSelection ? " has-selection" : ""}${
                      leaf && leafSelected ? " is-selected" : ""
                    }`}
                    role={leaf ? "checkbox" : undefined}
                    aria-checked={leaf ? leafSelected : undefined}
                    aria-disabled={leaf ? leafDisabled : undefined}
                    onClick={() => {
                      if (leaf) {
                        if (leafDisabled) {
                          return;
                        }
                        handleCheckboxChange(
                          capability.id,
                          null,
                          !leafSelected
                        );
                        return;
                      }
                      toggleVisibility(capability.id);
                    }}
                    onKeyDown={
                      leaf
                        ? (event) => {
                            if (leafDisabled) {
                              return;
                            }
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              handleCheckboxChange(
                                capability.id,
                                null,
                                !leafSelected
                              );
                            }
                          }
                        : undefined
                    }
                    tabIndex={leaf && !leafDisabled ? 0 : undefined}
                  >
                    {leaf ? (
                      <div
                        className="cap-checkbox-input"
                        onClick={(event) => event.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          className="cap-real-checkbox"
                          checked={leafSelected}
                          disabled={leafDisabled}
                          onChange={(event) =>
                            handleCheckboxChange(
                              capability.id,
                              null,
                              event.target.checked
                            )
                          }
                          aria-label={`Select ${capability.capabilityName}`}
                        />
                        <div className="cap-bg"></div>
                      </div>
                    ) : null}
                    <div className="cap-strategic-management-parent">
                      <div className="cap-strategic-management">
                        {capability.capabilityName}
                      </div>
                      <div className="cap-focuses-on-planning">
                        {capability.capabilityDescription}
                      </div>
                    </div>
                    <div className="cap-frame-wrapper2">
                      <div className="cap-selected-parent">
                        <div className="cap-selected">
                          {`${capabilitySelectedCount}/${selectableUnitCount(
                            capability
                          )} selected`}
                        </div>
                        {!leaf ? (
                          <img
                            className={`cap-frame-item${
                              isCapabilityExpanded ? " is-expanded" : ""
                            }`}
                            alt=""
                            aria-hidden="true"
                            src={`${basename}/images/plus.webp`}
                          />
                        ) : null}
                      </div>
                    </div>
                  </div>
                  {!leaf && isCapabilityExpanded && (
                    <div className="cap-frame-wrapper12">
                      <div className="cap-frame-parent6">
                        <div className="cap-frame-1000005422">
                          {(capability.subCapabilities ?? []).map((subCapability) => {
                            const isSelected = selectedCapabilities.some(
                              (item) =>
                                sameAssessmentUnit(
                                  item.capabilityId,
                                  item.subCapabilityId,
                                  capability.id,
                                  subCapability.subId
                                )
                            );
                            const isApproved = selectedCapabilities.some(
                              (item) =>
                                sameAssessmentUnit(
                                  item.capabilityId,
                                  item.subCapabilityId,
                                  capability.id,
                                  subCapability.subId
                                ) &&
                                (item.isApproved || item.state === "4")
                            );
                            const isItemLocked = selectedCapabilities.some(
                              (item) =>
                                sameAssessmentUnit(
                                  item.capabilityId,
                                  item.subCapabilityId,
                                  capability.id,
                                  subCapability.subId
                                ) &&
                                item.isNewLocked &&
                                requestStatus !== "Not Requested"
                            );
                            const isDisabled =
                              isApproved ||
                              isItemLocked ||
                              isWorkbookLocked ||
                              disableAllSelection;

                            return (
                              <div
                                className={`cap-checkbox-parent${
                                  isSelected ? " is-selected" : ""
                                }${isDisabled ? " is-disabled" : ""}`}
                                key={subCapability.subId}
                                role="checkbox"
                                aria-checked={isSelected}
                                aria-disabled={isDisabled}
                                tabIndex={isDisabled ? -1 : 0}
                                onClick={() => {
                                  if (isDisabled) {
                                    return;
                                  }
                                  handleCheckboxChange(
                                    capability.id,
                                    subCapability.subId,
                                    !isSelected
                                  );
                                }}
                                onKeyDown={(event) => {
                                  if (isDisabled) {
                                    return;
                                  }
                                  if (event.key === "Enter" || event.key === " ") {
                                    event.preventDefault();
                                    handleCheckboxChange(
                                      capability.id,
                                      subCapability.subId,
                                      !isSelected
                                    );
                                  }
                                }}
                              >
                                <div className="cap-checkbox">
                                  <div className="cap-checkbox-input">
                                    <input
                                      type="checkbox"
                                      className="cap-real-checkbox"
                                      value={subCapability.subId}
                                      onClick={(event) => event.stopPropagation()}
                                      onChange={(event) =>
                                        handleCheckboxChange(
                                          capability.id,
                                          subCapability.subId,
                                          event.target.checked
                                        )
                                      }
                                      checked={isSelected}
                                      disabled={isDisabled}
                                      tabIndex={-1}
                                      aria-hidden="true"
                                    />
                                    <div className="cap-bg"></div>
                                  </div>
                                  <div className="cap-strategic-management-parent">
                                    <div className="cap-data-ai">
                                      {subCapability.subCapabilityName}
                                    </div>
                                    <div className="Sub-cap-data-ai">
                                      {subCapability.subCapabilityDescription}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <ClientAppShell
      title="Import / Export"
      dataNav="import-export"
      selectedItem={selectedItem}
      onMenuSelect={handleMenuSelection}
      containerClassName="cap-selection-page"
      intro={{
        eyebrow: 'Offline workflow',
        description:
          'Download an Excel workbook to complete ratings offline, import your answers here, then submit to your advisor when every selected sub-capability is finished.',
      }}
    >
      <div className="dash-frame-wrapper">
        <div className="dash-frame-div">
          <div className="impexp-page">
            {isWorkbookLocked && (
              <div className="impexp-lock-banner">
                <CapabilityAccessBanner
                  variant={
                    requestStatus === "Pending"
                      ? "pending"
                      : requestStatus === "Rejected"
                        ? "rejected"
                        : requestStatus === "Approved" && isRequestActive
                          ? "unlocked"
                          : "locked"
                  }
                  onRequest={handleCapabilityRequest}
                  requesting={requesting}
                  ctaTo={
                    requestStatus === "Approved" && isRequestActive
                      ? "/capabilities"
                      : undefined
                  }
                  ctaLabel={
                    requestStatus === "Approved" && isRequestActive
                      ? "Select capabilities"
                      : undefined
                  }
                />
              </div>
            )}
            <section className="impexp-export">
              <div className="impexp-export__header">
                <button
                  type="button"
                  className="impexp-picker-toggle"
                  aria-expanded={isPickerOpen}
                  aria-controls="impexp-capability-picker"
                  onClick={() => setIsPickerOpen((open) => !open)}
                >
                  <div className="dash-action-card__body">
                    <p className="dash-action-card__title">Export workbook</p>
                    <p className="dash-action-card__text">
                      Choose the same capabilities as on the Capabilities page.
                      Changes here are saved to your project when you export.
                      {selectedCapabilities.length > 0
                        ? ` ${selectedCapabilities.length} selected.`
                        : ""}
                      {hasSelectionChanged ? " Selection updated." : ""}
                    </p>
                  </div>
                  <img
                    src={`${basename}/images/down.svg`}
                    alt=""
                    aria-hidden="true"
                    className={`impexp-picker-arrow${isPickerOpen ? " is-expanded" : ""}`}
                  />
                </button>
                <button
                  type="button"
                  className="btn btn-dashboard-primary dash-action-btn"
                  data-element="assessment-export"
                  onClick={handleExport}
                  disabled={!canExport}
                >
                  {isExporting ? "Exporting…" : "Export"}
                </button>
              </div>

              {isPickerOpen && (
                <div id="impexp-capability-picker">{renderPicker()}</div>
              )}
            </section>

            <div className="dash-action-card">
              <div className="dash-action-card__body">
                <p className="dash-action-card__title">Import workbook</p>
                <p className="dash-action-card__text">
                  Upload a completed Excel file to save answers into this
                  assessment. When every selected sub-capability is filled, you
                  can submit to your advisor here.
                </p>
              </div>
              <div className="impexp-import-actions">
                <button
                  type="button"
                  className="btn btn-dashboard-primary dash-action-btn"
                  data-element="assessment-import"
                  onClick={handleImportClick}
                  disabled={isImporting || isWorkbookLocked || isSubmitting}
                >
                  {isImporting ? "Importing…" : "Import"}
                </button>
                {canSubmitToAdvisor && (
                  <button
                    type="button"
                    className="btn btn-dashboard-primary dash-action-btn"
                    data-element="assessment-import-submit"
                    onClick={handleSubmitToAdvisor}
                    disabled={isSubmitting || isImporting || isWorkbookLocked}
                  >
                    {isSubmitting ? "Submitting…" : "Submit"}
                  </button>
                )}
              </div>
              <input
                ref={importInputRef}
                type="file"
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                hidden
                onChange={handleImportFile}
              />
            </div>
          </div>
        </div>
      </div>
    </ClientAppShell>
  );
};

export default ImportExport;
